import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { vendorQueries } from '../../lib/queries';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { T, sans, card, topBtn, linkBtn } from '../Vendors/VendorDetails';
import { CompanyLogo, categoryColor } from '../Vendors/VendorFourthParties';

/**
 * Portfolio-wide fourth-party concentration: which outside companies several of the tenant's vendors depend on at
 * once. Read-only view over backend/services/fourth_party_portfolio.py (no new detection, never touches scoring).
 * Layout follows the Vendors / Pre Onboarding pages: full-width header bar, KPI tiles, a filterable table with a
 * slide-over detail panel, and insight cards beside it.
 */

/* ─── Types ──────────────────────────────────────────────────────── */
interface VendorRef { id: string; name: string; domain: string; tier: string | null }
interface ProductRow { product: string; category: string; vendor_count: number }
interface Company {
  company_name: string; company_domain: string; commodity: boolean;
  vendor_count: number; critical_high_count: number;
  categories: string[]; products: ProductRow[]; vendors: VendorRef[];
}
interface CategoryRow { category: string; vendor_count: number; company_count: number }
interface Payload {
  companies: Company[];
  by_category: CategoryRow[];
  summary: {
    total_vendors: number; companies_with_shared_dependency: number;
    vendors_with_shared_dependency: number; top_concentration: number;
    top_non_commodity: Company | null;
  };
}

type View = 'all' | 'critical' | 'specialised' | 'commodity';
type SortKey = 'rank' | 'name' | 'vendors' | 'share' | 'critical';
type Exposure = 'Critical' | 'High' | 'Medium' | 'Low' | 'Commodity';

const APP_HEADER_HEIGHT = 64;
const PANEL_SLIDE_MS = 180;

/* ─── Theme ──────────────────────────────────────────────────────── */
const palette = (isDark: boolean) => ({
  isDark,
  ink: isDark ? '#e2e8f0' : T.ink,
  body: isDark ? '#cbd5e1' : T.ink2,
  slate: isDark ? '#94a3b8' : T.slate,
  muted: isDark ? '#7c8aa0' : T.muted,
  line: isDark ? '#1f1f1f' : T.bdr,
  line2: isDark ? '#171717' : '#EEF2F6',
  surface: isDark ? '#0d0d0d' : T.surface,
  surface2: isDark ? '#141414' : T.surface2,
  hover: isDark ? '#121212' : '#F8FAFC',
  bg: isDark ? '#000000' : T.bg,
  accent: isDark ? '#3B82F6' : '#2563EB',
  link: isDark ? '#60A5FA' : T.blue,
});
type P = ReturnType<typeof palette>;

const EXPOSURE: Record<Exposure, { color: string; label: string }> = {
  Critical: { color: '#DC2626', label: 'Critical' },
  High: { color: '#EA580C', label: 'High' },
  Medium: { color: '#D97706', label: 'Medium' },
  Low: { color: '#16A34A', label: 'Low' },
  Commodity: { color: '#64748B', label: 'Commodity' },
};
const EXPOSURE_ORDER: Exposure[] = ['Critical', 'High', 'Medium', 'Low', 'Commodity'];

const TIER: Record<string, string> = { Critical: '#DC2626', High: '#EA580C', Medium: '#D97706', Low: '#16A34A' };

/**
 * Exposure of the portfolio to one shared company -- deterministic, explained in the "How this works" dialog.
 * Commodity infrastructure is its own bucket; otherwise critical/high-tier vendors sharing it come first, then the
 * share of the whole portfolio that depends on it.
 */
function exposureOf(c: Company, total: number): Exposure {
  if (c.commodity) return 'Commodity';
  const share = total ? c.vendor_count / total : 0;
  if (c.critical_high_count >= 2) return 'Critical';
  if (c.critical_high_count === 1 || share >= 0.5) return 'High';
  if (share >= 0.25) return 'Medium';
  return 'Low';
}

const pct = (n: number) => `${Math.round(n * 100)}%`;
const plural = (n: number, w: string) =>
  `${n} ${n === 1 ? w : w.endsWith('y') && !/[aeiou]y$/.test(w) ? `${w.slice(0, -1)}ies` : `${w}s`}`;
const csvCell = (v: unknown) => {
  let s = v === null || v === undefined ? '' : String(v);
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/* ─── Small pieces ───────────────────────────────────────────────── */
function Pill({ label, color, c, solid = false }: { label: string; color: string; c: P; solid?: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, lineHeight: '18px',
      padding: '0 8px', borderRadius: 99, whiteSpace: 'nowrap',
      background: solid ? color : `${color}14`, color: solid ? '#fff' : color, border: `1px solid ${solid ? color : `${color}40`}`,
    }}>
      {!solid && <span style={{ width: 6, height: 6, borderRadius: 3, background: color }} />}
      {label}
    </span>
  );
}

function CategoryChip({ category, c }: { category: string; c: P }) {
  const col = categoryColor(category);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: c.body, whiteSpace: 'nowrap',
      padding: '1px 7px', borderRadius: 5, border: `1px solid ${c.line}`, background: c.surface,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: col }} />{category}
    </span>
  );
}

function Meter({ value, color, c, height = 6 }: { value: number; color: string; c: P; height?: number }) {
  return (
    <div style={{ height, borderRadius: 99, background: c.line2, overflow: 'hidden', flex: 1, minWidth: 40 }}>
      <div style={{ width: `${Math.max(2, Math.min(100, value * 100))}%`, height: '100%', background: color, borderRadius: 99 }} />
    </div>
  );
}

function AvatarStack({ vendors, c, max = 4 }: { vendors: VendorRef[]; c: P; max?: number }) {
  const shown = vendors.slice(0, max);
  const extra = vendors.length - shown.length;
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {shown.map((v, i) => (
        <span key={v.id} title={v.name} style={{
          marginLeft: i ? -7 : 0, width: 24, height: 24, borderRadius: 99, background: c.surface,
          border: `2px solid ${c.surface}`, boxShadow: `0 0 0 1px ${c.line}`, display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', overflow: 'hidden', zIndex: max - i,
        }}>
          <CompanyLogo name={v.name} domain={v.domain} isDark={c.isDark} size={18} />
        </span>
      ))}
      {extra > 0 && (
        <span style={{
          marginLeft: -7, height: 24, minWidth: 24, padding: '0 6px', borderRadius: 99, background: c.surface2,
          border: `2px solid ${c.surface}`, boxShadow: `0 0 0 1px ${c.line}`, fontSize: 10.5, fontWeight: 700,
          color: c.slate, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>+{extra}</span>
      )}
    </div>
  );
}

function SectionCard({ title, subtitle, action, children, c }: {
  title: string; subtitle?: string; action?: React.ReactNode; children: React.ReactNode; c: P;
}) {
  return (
    <div style={{ ...card(c.isDark), padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.ink }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11.5, color: c.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
      <div style={{ padding: '12px 16px' }}>{children}</div>
    </div>
  );
}

/* ─── Detail panel ───────────────────────────────────────────────── */
function DetailPanel({ comp, total, c, onClose, onOpenVendor }: {
  comp: Company; total: number; c: P; onClose: () => void; onOpenVendor: (id: string) => void;
}) {
  const exp = exposureOf(comp, total);
  const share = total ? comp.vendor_count / total : 0;
  const unclassified = comp.vendors.filter(v => !v.tier).length;
  const byCategory = useMemo(() => {
    const m = new Map<string, ProductRow[]>();
    comp.products.forEach(p => m.set(p.category, [...(m.get(p.category) || []), p]));
    return Array.from(m.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [comp]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const statBox = (label: string, value: React.ReactNode, sub?: string) => (
    <div style={{ border: `1px solid ${c.line}`, borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: c.ink, marginTop: 4, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: c.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <aside data-fp-panel role="dialog" aria-label={`${comp.company_name} details`} style={{
      position: 'fixed', top: APP_HEADER_HEIGHT, right: 0, bottom: 0, width: 'min(460px, 100vw)', zIndex: 1150,
      background: c.surface, borderLeft: `1px solid ${c.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans,
      animation: `fpPanelIn ${PANEL_SLIDE_MS}ms ease-out forwards`,
    }}>
      <div style={{ padding: '16px 18px', borderBottom: `1px solid ${c.line}`, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <CompanyLogo name={comp.company_name} domain={comp.company_domain} isDark={c.isDark} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>{comp.company_name}</div>
          <a href={`https://${comp.company_domain}`} target="_blank" rel="noopener noreferrer"
             style={{ fontSize: 12, color: c.link, textDecoration: 'none' }}>{comp.company_domain} ↗</a>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            <Pill label={`${EXPOSURE[exp].label} exposure`} color={EXPOSURE[exp].color} c={c} />
            {comp.commodity && <Pill label="Commodity infrastructure" color="#64748B" c={c} />}
          </div>
        </div>
        <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: c.muted, lineHeight: 1 }}>×</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'grid', gap: 18, alignContent: 'start' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {statBox('Vendors', comp.vendor_count, `of ${total} in portfolio`)}
          {statBox('Share', pct(share), 'of your portfolio')}
          {statBox('Critical / High', comp.critical_high_count, 'tier vendors')}
        </div>

        <div style={{ fontSize: 12.5, color: c.body, lineHeight: 1.55, padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.line}` }}>
          {comp.commodity
            ? <>An outage or breach at <strong>{comp.company_name}</strong> would touch {plural(comp.vendor_count, 'vendor')}, but it is mainstream infrastructure most companies rely on — worth knowing, rarely a finding on its own.</>
            : <>An outage or breach at <strong>{comp.company_name}</strong> would affect {plural(comp.vendor_count, 'vendor')} at once ({pct(share)} of your portfolio){comp.critical_high_count ? <>, including <strong>{plural(comp.critical_high_count, 'critical/high-tier vendor')}</strong></> : null}. Consider asking these vendors about their resilience plans for this dependency.</>}
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Vendors relying on it
          </div>
          <div style={{ border: `1px solid ${c.line}`, borderRadius: 8, overflow: 'hidden' }}>
            {comp.vendors.map((v, i) => (
              <button key={v.id} onClick={() => onOpenVendor(v.id)} style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', textAlign: 'left',
                background: 'none', border: 'none', borderTop: i ? `1px solid ${c.line2}` : 'none', cursor: 'pointer', fontFamily: sans,
              }}>
                <CompanyLogo name={v.name} domain={v.domain} isDark={c.isDark} size={22} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: c.ink }}>{v.name}</span>
                  <span style={{ display: 'block', fontSize: 11, color: c.muted }}>{v.domain}</span>
                </span>
                {v.tier ? <Pill label={`${v.tier} tier`} color={TIER[v.tier] || '#64748B'} c={c} />
                        : <span style={{ fontSize: 11, color: c.muted }}>Not classified</span>}
                <span style={{ color: c.muted, fontSize: 14 }}>›</span>
              </button>
            ))}
          </div>
          {unclassified > 0 && (
            <div style={{ fontSize: 11.5, color: c.muted, marginTop: 6 }}>
              {plural(unclassified, 'vendor')} {unclassified === 1 ? 'has' : 'have'} no inherent-risk classification yet — set it on the vendor's Profile tab to rank this more accurately.
            </div>
          )}
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Products in use ({comp.products.length})
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            {byCategory.map(([cat, prods]) => (
              <div key={cat}>
                <div style={{ marginBottom: 6 }}><CategoryChip category={cat} c={c} /></div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {prods.map(p => (
                    <div key={p.product} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: c.ink }}>
                      <span style={{ flex: '0 0 45%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.product}>{p.product}</span>
                      <Meter value={comp.vendor_count ? p.vendor_count / comp.vendor_count : 0} color={categoryColor(cat)} c={c} />
                      <span style={{ fontSize: 11.5, color: c.slate, minWidth: 54, textAlign: 'right' }}>{plural(p.vendor_count, 'vendor')}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}

/* ─── How this works ─────────────────────────────────────────────── */
function HowItWorks({ c, onClose }: { c: P; onClose: () => void }) {
  const row = (label: string, color: string, text: string) => (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <span style={{ width: 92, flexShrink: 0 }}><Pill label={label} color={color} c={c} /></span>
      <span style={{ fontSize: 12.5, color: c.body }}>{text}</span>
    </div>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card(c.isDark), maxWidth: 560, width: '100%', padding: 0, fontFamily: sans }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>How fourth-party concentration works</div>
          <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: c.muted }}>×</button>
        </div>
        <div style={{ padding: '16px 20px', display: 'grid', gap: 14, fontSize: 12.5, color: c.body, lineHeight: 1.55 }}>
          <p style={{ margin: 0 }}>
            Every vendor's Fourth Parties tab lists the outside companies it depends on. This page combines them across your whole
            portfolio and shows the companies that <strong>two or more</strong> of your vendors rely on — the ones whose outage or
            breach would hit several vendors at once. Nothing new is scanned, and it never changes any risk score.
          </p>
          <div style={{ display: 'grid', gap: 8 }}>
            {row('Critical', EXPOSURE.Critical.color, 'Two or more of the affected vendors are classified High or Critical inherent risk.')}
            {row('High', EXPOSURE.High.color, 'One High/Critical-tier vendor is affected, or at least half of your portfolio relies on it.')}
            {row('Medium', EXPOSURE.Medium.color, 'At least a quarter of your portfolio relies on it.')}
            {row('Low', EXPOSURE.Low.color, 'Shared by a small part of your portfolio.')}
            {row('Commodity', EXPOSURE.Commodity.color, 'AWS, Google, Microsoft, Cloudflare, Fastly, Akamai, GoDaddy, Namecheap — mainstream infrastructure almost every company uses. Shown, but kept apart so it does not drown out unusual dependencies.')}
          </div>
          <p style={{ margin: 0 }}>
            Inherent-risk tier comes from each vendor's Profile (data sensitivity, business criticality, system access). Vendors without
            a classification are shown as "Not classified" rather than guessed.
          </p>
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, textAlign: 'right' }}>
          <button onClick={onClose} style={{ ...topBtn(true, c.isDark), display: 'inline-flex' }}>Got it</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────── */
export default function FourthPartiesPortfolio() {
  const navigate = useNavigate();
  const { mode } = useCustomTheme();
  const c = palette(mode === 'dark');

  const [view, setView] = useState<View>('all');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'rank', dir: 1 });
  const [selected, setSelected] = useState<string | null>(null);
  const [howItWorks, setHowItWorks] = useState(false);

  const query = useQuery(vendorQueries.fourthPartiesPortfolio());
  const data = query.data as Payload | undefined;
  const total = data?.summary.total_vendors ?? 0;
  const all = useMemo(() => data?.companies ?? [], [data]);

  const counts = useMemo(() => ({
    all: all.length,
    critical: all.filter(x => x.critical_high_count > 0).length,
    specialised: all.filter(x => !x.commodity).length,
    commodity: all.filter(x => x.commodity).length,
  }), [all]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = all.filter(x =>
      view === 'all' ? true : view === 'critical' ? x.critical_high_count > 0 : view === 'specialised' ? !x.commodity : x.commodity);
    if (category) list = list.filter(x => x.categories.includes(category));
    if (q) list = list.filter(x =>
      x.company_name.toLowerCase().includes(q) || x.company_domain.includes(q) ||
      x.products.some(p => p.product.toLowerCase().includes(q)) ||
      x.vendors.some(v => (v.name || '').toLowerCase().includes(q)));
    if (sort.key !== 'rank') {
      const val = (x: Company) => sort.key === 'name' ? x.company_name.toLowerCase()
        : sort.key === 'vendors' || sort.key === 'share' ? x.vendor_count : x.critical_high_count;
      list = [...list].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * sort.dir);
    } else {
      // default: non-commodity before commodity, then the backend's own ranking (critical/high count, vendor count)
      list = [...list].sort((a, b) => Number(a.commodity) - Number(b.commodity));
    }
    return list;
  }, [all, view, category, search, sort]);

  const exposureCounts = useMemo(() => {
    const m: Record<Exposure, number> = { Critical: 0, High: 0, Medium: 0, Low: 0, Commodity: 0 };
    all.forEach(x => { m[exposureOf(x, total)] += 1; });
    return m;
  }, [all, total]);

  // vendors ranked by how many shared dependencies they sit on
  const exposedVendors = useMemo(() => {
    const m = new Map<string, { v: VendorRef; total: number; specialised: number }>();
    all.forEach(x => x.vendors.forEach(v => {
      const e = m.get(v.id) || { v, total: 0, specialised: 0 };
      e.total += 1; if (!x.commodity) e.specialised += 1;
      m.set(v.id, e);
    }));
    return Array.from(m.values()).sort((a, b) => b.specialised - a.specialised || b.total - a.total).slice(0, 6);
  }, [all]);

  const unclassified = useMemo(() => {
    const ids = new Set<string>();
    all.forEach(x => x.vendors.forEach(v => { if (!v.tier) ids.add(v.id); }));
    return ids.size;
  }, [all]);

  const selectedCompany = all.find(x => x.company_domain === selected) || null;
  const maxCat = Math.max(1, ...(data?.by_category ?? []).map(r => r.vendor_count));
  const topSpecial = all.find(x => !x.commodity) || null;

  const toggleSort = (key: SortKey) =>
    setSort(s => s.key === key ? { key, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { key, dir: key === 'name' ? 1 : -1 });

  const exportCsv = () => {
    const header = ['Company', 'Domain', 'Exposure', 'Commodity', 'Vendors', 'Share of portfolio', 'Critical/High-tier vendors', 'Categories', 'Products', 'Vendor names'];
    const lines = rows.map(x => [
      x.company_name, x.company_domain, exposureOf(x, total), x.commodity ? 'Yes' : 'No', x.vendor_count,
      pct(total ? x.vendor_count / total : 0), x.critical_high_count, x.categories.join('; '),
      x.products.map(p => p.product).join('; '), x.vendors.map(v => v.name).join('; '),
    ].map(csvCell).join(','));
    const blob = new Blob(['﻿' + [header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `fourth-party-concentration-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // close the panel when clicking outside it
  useEffect(() => {
    if (!selected) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest('[data-fp-panel]') && !t.closest('[data-fp-row]')) setSelected(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [selected]);

  const views: { key: View; label: string; count: number }[] = [
    { key: 'all', label: 'All shared', count: counts.all },
    { key: 'critical', label: 'Critical vendors exposed', count: counts.critical },
    { key: 'specialised', label: 'Specialised', count: counts.specialised },
    { key: 'commodity', label: 'Commodity', count: counts.commodity },
  ];

  const thStyle = (key?: SortKey, align: 'left' | 'right' = 'left'): React.CSSProperties => ({
    padding: '9px 14px', textAlign: align, fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
    color: sort.key === key ? c.ink : c.muted, background: c.surface2, borderBottom: `1px solid ${c.line}`,
    cursor: key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap',
  });
  const arrow = (key: SortKey) => sort.key === key ? (sort.dir === 1 ? ' ↑' : ' ↓') : '';
  const tdStyle: React.CSSProperties = { padding: '11px 14px', fontSize: 12.5, color: c.ink, borderBottom: `1px solid ${c.line2}`, verticalAlign: 'middle' };
  const inputStyle: React.CSSProperties = {
    height: 32, padding: '0 10px', borderRadius: 7, border: `1px solid ${c.line}`, background: c.surface,
    color: c.ink, fontSize: 12.5, fontFamily: sans, outline: 'none',
  };

  const kpis = [
    { label: 'Shared dependencies', value: counts.all, sub: 'companies 2+ vendors rely on', color: c.ink, onClick: () => setView('all') },
    { label: 'Critical vendors exposed', value: counts.critical, sub: 'shared deps touching high/critical-tier vendors', color: counts.critical ? '#DC2626' : c.ink, onClick: () => setView('critical') },
    { label: 'Specialised', value: counts.specialised, sub: 'non-commodity shared dependencies', color: c.ink, onClick: () => setView('specialised') },
    { label: 'Vendors affected', value: data ? `${data.summary.vendors_with_shared_dependency}/${total}` : '—', sub: 'share at least one dependency', color: c.ink, onClick: () => setView('all') },
    { label: 'Top specialised', value: topSpecial ? pct(total ? topSpecial.vendor_count / total : 0) : '—', sub: topSpecial ? `of vendors use ${topSpecial.company_name}` : 'none found', color: c.ink, onClick: () => topSpecial && setSelected(topSpecial.company_domain) },
  ];

  return (
    <div style={{ fontFamily: sans, background: c.bg, minHeight: '100vh' }}>
      <style>{`
        @keyframes fpPanelIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @media (prefers-reduced-motion: reduce) { aside[data-fp-panel] { animation-duration: 1ms !important; } }
        .fp-main { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 16px; align-items: start; }
        @media (max-width: 1180px) { .fp-main { grid-template-columns: minmax(0, 1fr); } }
        .fp-row:hover td { background: ${c.hover}; }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        padding: '16px 24px', borderBottom: `1px solid ${c.line}`, background: c.surface,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: c.ink, margin: 0 }}>Fourth Parties</h1>
          <p style={{ fontSize: 12, color: c.muted, margin: '2px 0 0' }}>
            The outside companies several of your vendors depend on at once — your portfolio's shared points of failure.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={topBtn(false, c.isDark)} onClick={() => setHowItWorks(true)}>How this works</button>
          <button style={{ ...topBtn(false, c.isDark), opacity: rows.length ? 1 : 0.5 }} onClick={exportCsv} disabled={!rows.length}>Export CSV</button>
        </div>
      </div>

      <div style={{ padding: '20px 24px', display: 'grid', gap: 16 }}>
        {query.isPending ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
              {[0, 1, 2, 3, 4].map(i => <div key={i} style={{ ...card(c.isDark), height: 92, background: c.surface2 }} />)}
            </div>
            <div style={{ ...card(c.isDark), height: 320, background: c.surface2 }} />
          </div>
        ) : query.isError || !data ? (
          <div style={{ ...card(c.isDark), color: '#DC2626', fontSize: 12.5 }}>
            Could not load fourth-party concentration. <button style={linkBtn(c.isDark)} onClick={() => query.refetch()}>Retry</button>
          </div>
        ) : (
          <>
            {/* ── KPIs ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
              {kpis.map(k => (
                <button key={k.label} onClick={k.onClick} style={{ ...card(c.isDark), textAlign: 'left', cursor: 'pointer', fontFamily: sans, padding: '14px 16px' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>{k.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: k.color, marginTop: 6, lineHeight: 1 }}>{k.value}</div>
                  <div style={{ fontSize: 11.5, color: c.muted, marginTop: 6, lineHeight: 1.35 }}>{k.sub}</div>
                </button>
              ))}
            </div>

            {unclassified > 0 && (
              <div role="status" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                padding: '10px 14px', borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface, fontSize: 12.5, color: c.body,
              }}>
                <span>
                  <strong style={{ color: c.ink }}>{plural(unclassified, 'vendor')}</strong> in these dependencies {unclassified === 1 ? 'has' : 'have'} no
                  inherent-risk classification, so exposure is ranked on portfolio share alone. Classify them on each vendor's Profile tab.
                </span>
                <button style={linkBtn(c.isDark)} onClick={() => navigate('/vendors')}>Go to vendors</button>
              </div>
            )}

            <div className="fp-main">
              {/* ── Table ── */}
              <div style={{ ...card(c.isDark), padding: 0, overflow: 'hidden', minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 4, padding: '10px 12px', borderBottom: `1px solid ${c.line}`, overflowX: 'auto', scrollbarWidth: 'thin' }} role="tablist">
                  {views.map(v => {
                    const active = view === v.key;
                    return (
                      <button key={v.key} role="tab" aria-selected={active} onClick={() => setView(v.key)} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 99, border: 'none',
                        cursor: 'pointer', fontFamily: sans, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
                        background: active ? (c.isDark ? '#e2e8f0' : T.ink) : 'transparent', color: active ? (c.isDark ? '#0d0d0d' : '#fff') : c.slate,
                      }}>
                        {v.label}
                        <span style={{
                          fontSize: 10.5, fontWeight: 700, padding: '0 6px', borderRadius: 99, lineHeight: '17px',
                          background: v.key === 'critical' && v.count ? '#DC2626' : active ? (c.isDark ? '#0d0d0d22' : '#ffffff22') : c.surface2,
                          color: v.key === 'critical' && v.count ? '#fff' : 'inherit',
                        }}>{v.count}</span>
                      </button>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: `1px solid ${c.line}`, flexWrap: 'wrap' }}>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search company, product or vendor"
                         style={{ ...inputStyle, width: 280, maxWidth: '100%' }} />
                  <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...inputStyle, paddingRight: 6 }}>
                    <option value="">All categories</option>
                    {(data.by_category || []).map(r => <option key={r.category} value={r.category}>{r.category}</option>)}
                  </select>
                  {(search || category) && (
                    <button style={linkBtn(c.isDark)} onClick={() => { setSearch(''); setCategory(''); }}>Clear filters</button>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: c.muted }}>{plural(rows.length, 'company')}</span>
                </div>

                {all.length === 0 ? (
                  <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: c.ink }}>No shared dependencies yet</div>
                    <div style={{ fontSize: 12.5, color: c.muted, marginTop: 6, maxWidth: 440, marginInline: 'auto', lineHeight: 1.5 }}>
                      No outside company is used by two or more of your vendors. Fourth parties are detected after each vendor scan —
                      this fills in as your portfolio grows.
                    </div>
                  </div>
                ) : rows.length === 0 ? (
                  <div style={{ padding: '40px 24px', textAlign: 'center', fontSize: 12.5, color: c.muted }}>Nothing matches these filters.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                      <thead>
                        <tr>
                          <th style={thStyle('name')} onClick={() => toggleSort('name')}>Company{arrow('name')}</th>
                          <th style={thStyle()}>Categories</th>
                          <th style={thStyle('vendors')} onClick={() => toggleSort('vendors')}>Vendors{arrow('vendors')}</th>
                          <th style={{ ...thStyle('share'), width: 180 }} onClick={() => toggleSort('share')}>Share of portfolio{arrow('share')}</th>
                          <th style={thStyle('critical', 'right')} onClick={() => toggleSort('critical')}>Crit/High tier{arrow('critical')}</th>
                          <th style={thStyle()}>Exposure</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(x => {
                          const exp = exposureOf(x, total);
                          const share = total ? x.vendor_count / total : 0;
                          const active = selected === x.company_domain;
                          return (
                            <tr key={x.company_domain} data-fp-row className="fp-row" onClick={() => setSelected(active ? null : x.company_domain)}
                                style={{ cursor: 'pointer', opacity: x.commodity && view !== 'commodity' ? 0.78 : 1 }}>
                              <td style={{ ...tdStyle, background: active ? (c.isDark ? '#0f1e38' : '#EFF6FF') : undefined }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <CompanyLogo name={x.company_name} domain={x.company_domain} isDark={c.isDark} size={28} />
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, color: c.ink }}>{x.company_name}</div>
                                    <div style={{ fontSize: 11, color: c.muted, whiteSpace: 'nowrap' }}>{x.company_domain} · {plural(x.products.length, 'product')}</div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ ...tdStyle, background: active ? (c.isDark ? '#0f1e38' : '#EFF6FF') : undefined }}>
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 260 }}>
                                  {x.categories.slice(0, 2).map(cat => <CategoryChip key={cat} category={cat} c={c} />)}
                                  {x.categories.length > 2 && <span style={{ fontSize: 11, color: c.muted, alignSelf: 'center' }}>+{x.categories.length - 2}</span>}
                                </div>
                              </td>
                              <td style={{ ...tdStyle, background: active ? (c.isDark ? '#0f1e38' : '#EFF6FF') : undefined }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <AvatarStack vendors={x.vendors} c={c} />
                                  <span style={{ fontWeight: 600 }}>{x.vendor_count}</span>
                                </div>
                              </td>
                              <td style={{ ...tdStyle, background: active ? (c.isDark ? '#0f1e38' : '#EFF6FF') : undefined }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <Meter value={share} color={EXPOSURE[exp].color} c={c} />
                                  <span style={{ fontSize: 12, fontWeight: 600, minWidth: 36, textAlign: 'right' }}>{pct(share)}</span>
                                </div>
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'right', background: active ? (c.isDark ? '#0f1e38' : '#EFF6FF') : undefined }}>
                                <span style={{ fontWeight: 700, color: x.critical_high_count ? '#DC2626' : c.muted }}>{x.critical_high_count}</span>
                              </td>
                              <td style={{ ...tdStyle, background: active ? (c.isDark ? '#0f1e38' : '#EFF6FF') : undefined }}>
                                <Pill label={EXPOSURE[exp].label} color={EXPOSURE[exp].color} c={c} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ── Insights ── */}
              <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
                <SectionCard c={c} title="Exposure breakdown" subtitle="Shared dependencies by exposure level">
                  {all.length === 0 ? <div style={{ fontSize: 12, color: c.muted }}>Nothing to show yet.</div> : (
                    <>
                      <div style={{ display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden', background: c.line2, marginBottom: 12 }}>
                        {EXPOSURE_ORDER.filter(e => exposureCounts[e]).map(e => (
                          <div key={e} title={`${e}: ${exposureCounts[e]}`} style={{ width: `${(exposureCounts[e] / all.length) * 100}%`, background: EXPOSURE[e].color }} />
                        ))}
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {EXPOSURE_ORDER.map(e => (
                          <div key={e} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12.5, color: c.body }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 8, height: 8, borderRadius: 2, background: EXPOSURE[e].color }} />{e}
                            </span>
                            <span style={{ fontWeight: 600, color: exposureCounts[e] ? c.ink : c.muted }}>{exposureCounts[e]}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </SectionCard>

                <SectionCard c={c} title="Concentration by category" subtitle="Vendors sharing a dependency in each category">
                  {(data.by_category || []).length === 0 ? <div style={{ fontSize: 12, color: c.muted }}>Nothing to show yet.</div> : (
                    <div style={{ display: 'grid', gap: 9 }}>
                      {data.by_category.slice(0, 8).map(r => {
                        const active = category === r.category;
                        return (
                          <button key={r.category} onClick={() => setCategory(active ? '' : r.category)} style={{
                            display: 'grid', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: sans,
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: active ? c.accent : c.body, fontWeight: active ? 700 : 500 }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 7, height: 7, borderRadius: 4, background: categoryColor(r.category) }} />{r.category}
                              </span>
                              <span style={{ color: c.muted, whiteSpace: 'nowrap' }}>{plural(r.vendor_count, 'vendor')} · {plural(r.company_count, 'company')}</span>
                            </div>
                            <Meter value={r.vendor_count / maxCat} color={categoryColor(r.category)} c={c} height={5} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </SectionCard>

                <SectionCard c={c} title="Most exposed vendors" subtitle="Vendors sitting on the most shared dependencies">
                  {exposedVendors.length === 0 ? <div style={{ fontSize: 12, color: c.muted }}>Nothing to show yet.</div> : (
                    <div style={{ display: 'grid', gap: 2 }}>
                      {exposedVendors.map(({ v, total: t, specialised }) => (
                        <button key={v.id} onClick={() => navigate(`/vendors/${v.id}`, { state: { tab: 'fourth-parties' } })} style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '7px 4px', background: 'none', border: 'none',
                          borderRadius: 6, cursor: 'pointer', textAlign: 'left', fontFamily: sans,
                        }}>
                          <CompanyLogo name={v.name} domain={v.domain} isDark={c.isDark} size={22} />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</span>
                            <span style={{ display: 'block', fontSize: 11, color: c.muted }}>{specialised} specialised · {t - specialised} commodity</span>
                          </span>
                          {v.tier && <Pill label={v.tier} color={TIER[v.tier] || '#64748B'} c={c} />}
                          <span style={{ fontSize: 13, fontWeight: 700, color: c.ink, minWidth: 18, textAlign: 'right' }}>{t}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </SectionCard>
              </div>
            </div>

            <div style={{ fontSize: 11.5, color: c.muted }}>
              Built from each vendor's detected and manually added fourth parties. Informational only — it never changes a vendor's risk score.
            </div>
          </>
        )}
      </div>

      {selectedCompany && (
        <DetailPanel comp={selectedCompany} total={total} c={c} onClose={() => setSelected(null)}
          onOpenVendor={id => navigate(`/vendors/${id}`, { state: { tab: 'fourth-parties' } })} />
      )}
      {howItWorks && <HowItWorks c={c} onClose={() => setHowItWorks(false)} />}
    </div>
  );
}
